import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isWeb } from '../utils/platform';
import { getImageCapableModels, splitPrompts } from '../services/openRouterImageService';
import './ImageGenBulk.css';

const MAX_TASKS = 100;
const ASPECT_RATIO_OPTIONS = ['16:9', '4:3', '1:1', '3:4', '9:16'];
const FLOW_GENERATION_OPTIONS = ['x1', 'x2', 'x3', 'x4'];
const FLOW_MODEL_OPTIONS = [
  { value: 'nano_banana_2', label: 'Nano Banana 2' },
  { value: 'nano_banana_pro', label: 'Nano Banana Pro' },
  { value: 'imagen_4', label: 'Imagen 4' },
];

function taskId() {
  return `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function ImageGenBulk() {
  const imageGen = typeof window !== 'undefined' ? window.electronAPI?.imageGenBulk : null;
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [paused, setPaused] = useState(true);
  const [delaySeconds, setDelaySeconds] = useState(15);
  const [batchRestEvery, setBatchRestEvery] = useState(5);
  const [batchRestSeconds, setBatchRestSeconds] = useState(60);
  const [outputDir, setOutputDir] = useState('');
  const [currentBatchOutputDir, setCurrentBatchOutputDir] = useState('');
  const [mode, setMode] = useState('openrouter_api');
  const [selectedModel, setSelectedModel] = useState('');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [flowMediaType, setFlowMediaType] = useState('image');
  const [flowGenerationCount, setFlowGenerationCount] = useState('x1');
  const [flowModel, setFlowModel] = useState('nano_banana_2');
  const [models, setModels] = useState([]);
  const [bulkText, setBulkText] = useState('');
  const [notice, setNotice] = useState('');
  const [queueProgress, setQueueProgress] = useState(null);
  const [sharedRefs, setSharedRefs] = useState([]);
  const [diagLog, setDiagLog] = useState([]);
  const [activeTab, setActiveTab] = useState('inputs');
  const fileRef = useRef(null);

  const syncState = useCallback((payload) => {
    if (!payload) return;
    setTasks(Array.isArray(payload.tasks) ? payload.tasks : []);
    setPaused(payload.paused !== false);
    if (typeof payload.delaySeconds === 'number') setDelaySeconds(payload.delaySeconds);
    if (typeof payload.batchRestEvery === 'number') setBatchRestEvery(payload.batchRestEvery);
    if (typeof payload.batchRestSeconds === 'number') setBatchRestSeconds(payload.batchRestSeconds);
    if (typeof payload.outputDir === 'string') setOutputDir(payload.outputDir);
    if (typeof payload.currentBatchOutputDir === 'string') setCurrentBatchOutputDir(payload.currentBatchOutputDir);
    if (typeof payload.mode === 'string') setMode(payload.mode);
    if (typeof payload.selectedModel === 'string') setSelectedModel(payload.selectedModel);
    if (typeof payload.aspectRatio === 'string') setAspectRatio(payload.aspectRatio);
    if (typeof payload.flowMediaType === 'string') setFlowMediaType(payload.flowMediaType);
    if (typeof payload.flowGenerationCount === 'string') setFlowGenerationCount(payload.flowGenerationCount);
    if (typeof payload.flowModel === 'string') setFlowModel(payload.flowModel);
  }, []);

  const persistPatch = useCallback(
    async (patch) => {
      if (!imageGen) return;
      const res = await imageGen.setState(patch);
      if (res?.success && res.data) syncState(res.data);
    },
    [imageGen, syncState]
  );

  useEffect(() => {
    if (!imageGen) {
      setLoading(false);
      return;
    }
    let offTask;
    let offErr;
    let offProgress;
    let offDiag;
    (async () => {
      const res = await imageGen.getState();
      if (res?.success && res.data) syncState(res.data);
      setLoading(false);
    })();
    offTask = imageGen.onTaskUpdated((p) => syncState(p));
    offErr = imageGen.onAutomationError((p) => setNotice(p?.error || 'Automation error'));
    offProgress = imageGen.onQueueProgress((p) => setQueueProgress(p));
    offDiag =
      typeof imageGen.onAutomationDiagnostics === 'function'
        ? imageGen.onAutomationDiagnostics((p) => {
            if (!p) return;
            setDiagLog((prev) => [p, ...prev].slice(0, 60));
          })
        : null;
    return () => {
      if (typeof offTask === 'function') offTask();
      if (typeof offErr === 'function') offErr();
      if (typeof offProgress === 'function') offProgress();
      if (typeof offDiag === 'function') offDiag();
    };
  }, [imageGen, syncState]);

  useEffect(() => {
    const apiKey = localStorage.getItem('openrouter_api_key') || '';
    getImageCapableModels(apiKey)
      .then((list) => {
        setModels(list);
        if (!selectedModel && list.length > 0) {
          const first = list.find((m) => !m.disabled) || list[0];
          setSelectedModel(first.value);
          persistPatch({ selectedModel: first.value });
        }
      })
      .catch(() => setModels([]));
  }, [persistPatch, selectedModel]);

  const addBulkTasks = useCallback(() => {
    const lines = splitPrompts(bulkText);
    if (lines.length === 0) {
      setNotice('No prompts found. Add one prompt per line.');
      return;
    }
    const remaining = MAX_TASKS - tasks.length;
    if (remaining <= 0) {
      setNotice(`Queue limit reached (${MAX_TASKS}).`);
      return;
    }
    const selectedRefs = sharedRefs.slice(0, 6);
    const next = [
      ...tasks,
      ...lines.slice(0, remaining).map((prompt) => ({
        id: taskId(),
        prompt,
        status: 'queued',
        mode,
        model: selectedModel || '',
        referenceImagePaths: selectedRefs,
      })),
    ];
    persistPatch({ tasks: next, mode, selectedModel });
    setBulkText('');
    setNotice(`Added ${Math.min(lines.length, remaining)} image task(s).`);
  }, [bulkText, mode, persistPatch, selectedModel, sharedRefs, tasks]);

  const removeTask = (id) => {
    persistPatch({ tasks: tasks.filter((t) => t.id !== id) });
  };

  const chooseOutputDir = async () => {
    if (!imageGen) return;
    const res = await imageGen.chooseOutputDir();
    if (res?.success && res.data) syncState(res.data);
  };

  const pickReferences = async () => {
    if (!imageGen) return;
    const res = await imageGen.pickReferenceFiles();
    if (res?.success && Array.isArray(res.filePaths)) {
      setSharedRefs(res.filePaths);
      setNotice(`Selected ${res.filePaths.length} reference image(s).`);
    }
  };

  // Remove one reference image from a specific task (by index)
  const removeRefFromTask = async (taskId, refIndex) => {
    if (!imageGen) return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    const updated = (task.referenceImagePaths || []).filter((_, i) => i !== refIndex);
    await imageGen.updateTaskRefs(taskId, updated);
  };

  // Open file picker to add extra reference images to a specific task
  const addRefToTask = async (taskId) => {
    if (!imageGen) return;
    await imageGen.pickRefsForTask(taskId);
    // State update is broadcast by the main process via task-updated
  };

  const startPause = async () => {
    if (!imageGen) return;
    if (paused) {
      const openrouterApiKey = localStorage.getItem('openrouter_api_key') || '';
      const res = await imageGen.start({ openrouterApiKey });
      if (!res?.success) setNotice(res?.message || res?.error || 'Could not start queue.');
      else {
        if (mode === 'google_flow_automation') {
          await imageGen.togglePeek();
        }
        if (res.batchOutputDir) setNotice(`Started new project batch. Output folder: ${res.batchOutputDir}`);
      }
    } else {
      await imageGen.pause();
    }
  };

  const retryFailed = () => {
    // Reset both 'error' tasks and any 'running' tasks stuck from an interrupted session.
    const next = tasks.map((t) =>
      t.status === 'error' || t.status === 'running' ? { ...t, status: 'queued', error: null } : t
    );
    persistPatch({ tasks: next });
  };

  const hasStuckOrFailed = tasks.some((t) => t.status === 'error' || t.status === 'running');

  const progressText = useMemo(() => {
    if (!queueProgress) return '';
    if (queueProgress.phase === 'running') return `Running ${queueProgress.index}/${queueProgress.total} — ${queueProgress.promptPreview || ''}`.trimEnd();
    if (queueProgress.phase === 'delay') return `Waiting before task ${queueProgress.index}/${queueProgress.total}…`;
    if (queueProgress.phase === 'batch_rest') {
      const { remaining, tasksRemaining } = queueProgress;
      return `Batch rest — resuming in ${remaining}s  (${tasksRemaining} task${tasksRemaining !== 1 ? 's' : ''} remaining)`;
    }
    if (queueProgress.phase === 'all_complete') return 'All tasks complete.';
    return queueProgress.message || '';
  }, [queueProgress]);

  if (isWeb) {
    return (
      <div className="imggen-bulk">
        <h2>Image Generation</h2>
        <p>This feature is only available in the desktop app.</p>
      </div>
    );
  }

  if (!imageGen) {
    return (
      <div className="imggen-bulk">
        <h2>Image Generation</h2>
        <p>Electron bridge unavailable. Open the desktop app.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="imggen-bulk">
        <h2>Image Generation</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="imggen-bulk">
      <h2>Image Generation</h2>
      <p className="imggen-sub">Bulk-generate images via OpenRouter API or Google Flow automation.</p>

      {notice ? <div className="imggen-notice">{notice}</div> : null}

      <div className="imggen-tabs">
        <button
          type="button"
          className={`imggen-tab ${activeTab === 'inputs' ? 'active' : ''}`}
          onClick={() => setActiveTab('inputs')}
        >
          Inputs
        </button>
        <button
          type="button"
          className={`imggen-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
        <button
          type="button"
          className={`imggen-tab ${activeTab === 'diagnostics' ? 'active' : ''}`}
          onClick={() => setActiveTab('diagnostics')}
        >
          Diagnostics{diagLog.length > 0 ? ` (${diagLog.length})` : ''}
        </button>
      </div>

      {activeTab === 'inputs' ? (
        <>
          <div className="imggen-row">
            <label>
              Mode
              <select
                value={mode}
                onChange={(e) => {
                  const v = e.target.value;
                  setMode(v);
                  persistPatch({ mode: v });
                }}
              >
                <option value="openrouter_api">OpenRouter API</option>
                <option value="google_flow_automation">Google Flow Automation</option>
              </select>
            </label>

            <button type="button" className="imggen-btn secondary" onClick={chooseOutputDir}>
              Output folder...
            </button>
            {mode === 'google_flow_automation' ? (
              <button type="button" className="imggen-btn secondary" onClick={() => imageGen.togglePeek()}>
                Open / Hide Google Flow
              </button>
            ) : null}
          </div>

          {mode === 'openrouter_api' ? (
            <div className="imggen-row">
              <label className="imggen-model">
                OpenRouter Model
                <select
                  value={selectedModel}
                  onChange={(e) => {
                    setSelectedModel(e.target.value);
                    persistPatch({ selectedModel: e.target.value });
                  }}
                >
                  {models.map((m) => (
                    <option key={m.value} value={m.value} disabled={m.disabled}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          <div className="imggen-refs-section">
            <div className="imggen-refs-header">
              <span className="imggen-refs-label">
                Reference images
                <span className="imggen-refs-hint">
                  (applied to all prompts added below — optional)
                </span>
              </span>
              <div className="imggen-refs-actions">
                <button type="button" className="imggen-btn secondary" onClick={pickReferences}>
                  {sharedRefs.length > 0 ? 'Change references...' : 'Add reference images...'}
                </button>
                {sharedRefs.length > 0 && (
                  <button
                    type="button"
                    className="imggen-btn secondary"
                    onClick={() => setSharedRefs([])}
                    title="Remove selected reference images"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            {sharedRefs.length > 0 ? (
              <ul className="imggen-refs-list">
                {sharedRefs.map((p, i) => (
                  <li key={i} className="imggen-refs-item">
                    <span className="imggen-refs-icon">🖼</span>
                    <span className="imggen-refs-name" title={p}>
                      {p.split(/[\\/]/).pop()}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="imggen-help imggen-refs-empty">
                No reference images selected. When added, these images will be uploaded alongside every prompt in this batch to guide the style or content of generations.
              </p>
            )}
          </div>

          <div className="imggen-block">
            <h3>Prompt lines</h3>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={'One prompt per line\ne.g. Futuristic city skyline at sunset'}
            />
            <div className="imggen-row">
              <button type="button" className="imggen-btn primary" onClick={addBulkTasks}>
                Add lines to queue
              </button>
              <button type="button" className="imggen-btn secondary" onClick={() => fileRef.current?.click()}>
                Load prompts file...
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".txt,.csv,text/plain,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setBulkText(String(reader.result || ''));
                  reader.readAsText(file, 'UTF-8');
                }}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </>
      ) : null}

      {activeTab === 'settings' ? (
        <div className="imggen-block">
          <h3>Generation settings</h3>
          <p className="imggen-help">
            Flow uses all of these settings. OpenRouter API mode also uses the selected aspect ratio.
          </p>

          <div className="imggen-settings-grid">
            <label>
              Aspect ratio
              <select
                value={aspectRatio}
                onChange={(e) => {
                  setAspectRatio(e.target.value);
                  persistPatch({ aspectRatio: e.target.value });
                }}
              >
                {ASPECT_RATIO_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Image / video
              <select
                value={flowMediaType}
                onChange={(e) => {
                  setFlowMediaType(e.target.value);
                  persistPatch({ flowMediaType: e.target.value });
                }}
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
            </label>

            <label>
              Number of generation
              <select
                value={flowGenerationCount}
                onChange={(e) => {
                  setFlowGenerationCount(e.target.value);
                  persistPatch({ flowGenerationCount: e.target.value });
                }}
              >
                {FLOW_GENERATION_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label>
              AI model
              <select
                value={flowModel}
                onChange={(e) => {
                  setFlowModel(e.target.value);
                  persistPatch({ flowModel: e.target.value });
                }}
              >
                {FLOW_MODEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="imggen-block">
            <h3>Rate limiting</h3>
            <p className="imggen-help">
              Prevents Google Flow's "unusual activity" flag during long runs. Recommended: 15s delay, rest 60s every 5 tasks.
            </p>
            <div className="imggen-settings-grid">
              <label>
                Delay between tasks (s)
                <input
                  type="number"
                  min={0}
                  max={120}
                  value={delaySeconds}
                  onChange={(e) => setDelaySeconds(parseInt(e.target.value || '0', 10))}
                  onBlur={() => persistPatch({ delaySeconds: Number.isFinite(delaySeconds) ? delaySeconds : 15 })}
                />
              </label>

              <label>
                Rest every N tasks
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={batchRestEvery}
                  onChange={(e) => setBatchRestEvery(parseInt(e.target.value || '1', 10))}
                  onBlur={() => persistPatch({ batchRestEvery: Number.isFinite(batchRestEvery) ? Math.max(1, batchRestEvery) : 5 })}
                />
              </label>

              <label>
                Rest duration (s)
                <input
                  type="number"
                  min={0}
                  max={300}
                  value={batchRestSeconds}
                  onChange={(e) => setBatchRestSeconds(parseInt(e.target.value || '0', 10))}
                  onBlur={() => persistPatch({ batchRestSeconds: Number.isFinite(batchRestSeconds) ? batchRestSeconds : 60 })}
                />
              </label>
            </div>
          </div>
        </div>
      ) : null}

      {/* Queue controls, progress indicators, and the task list are part of the
          INPUT workflow and only live on the Inputs tab. The Settings tab has its
          own page, and the Diagnostics tab only shows the raw log so the user can
          see exactly what's happening without visual clutter. */}
      {activeTab === 'inputs' ? (
        <>
          <div className="imggen-row">
            <button type="button" className="imggen-btn primary" onClick={startPause}>
              {paused ? (mode === 'google_flow_automation' ? 'Start generation' : 'Start queue') : 'Pause queue'}
            </button>
            <button type="button" className="imggen-btn secondary" onClick={retryFailed} disabled={!hasStuckOrFailed}>
              {tasks.some((t) => t.status === 'running') ? 'Re-queue stuck & failed' : 'Retry failed'}
            </button>
            <button type="button" className="imggen-btn secondary" onClick={() => persistPatch({ tasks: [] })}>
              Clear queue
            </button>
          </div>

          {progressText ? <div className="imggen-help">{progressText}</div> : null}
          {outputDir ? <div className="imggen-help">Output: {outputDir}</div> : null}
          {currentBatchOutputDir ? <div className="imggen-help">Current project batch folder: {currentBatchOutputDir}</div> : null}

          <div className="imggen-list">
            {tasks.map((task) => (
              <div key={task.id} className="imggen-item">
                <div className={`status ${task.status || 'queued'}`}>{(task.status || 'queued').toUpperCase()}</div>
                <div className="body">
                  <div className="prompt">{task.prompt}</div>
                  {/* Per-task reference images — visible only when at least one ref is attached
                      OR when the task is queued/error so the user can still edit */}
                  {(task.referenceImagePaths?.length > 0 || task.status === 'queued' || task.status === 'error') && (
                    <div className="imggen-task-refs">
                      {(task.referenceImagePaths || []).map((p, i) => (
                        <span key={i} className="imggen-task-ref-chip" title={p}>
                          <span className="imggen-task-ref-name">🖼 {p.split(/[\\/]/).pop()}</span>
                          {(task.status === 'queued' || task.status === 'error') && (
                            <button
                              type="button"
                              className="imggen-task-ref-remove"
                              title="Remove this reference from task"
                              onClick={() => removeRefFromTask(task.id, i)}
                            >
                              ✕
                            </button>
                          )}
                        </span>
                      ))}
                      {(task.status === 'queued' || task.status === 'error') && (
                        <button
                          type="button"
                          className="imggen-task-ref-add"
                          title="Add reference image(s) to this task"
                          onClick={() => addRefToTask(task.id)}
                        >
                          + ref
                        </button>
                      )}
                    </div>
                  )}
                  <div className="meta">
                    {task.mode === 'google_flow_automation' ? 'Automation' : task.model || 'OpenRouter model'}
                    {task.error ? ` · ${task.error}` : ''}
                  </div>
                </div>
                <button type="button" className="remove" onClick={() => removeTask(task.id)}>
                  x
                </button>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {activeTab === 'diagnostics' ? (
        <div className="imggen-diag">
          <div className="imggen-diag-head">
            <h3>Diagnostics</h3>
            <button type="button" className="imggen-btn secondary" onClick={() => setDiagLog([])}>
              Clear log
            </button>
          </div>
          {diagLog.length === 0 ? (
            <p className="imggen-help">No diagnostics yet. Start a queue run to populate this log.</p>
          ) : (
            <pre className="imggen-diag-log" tabIndex={0}>
              {diagLog.map((entry, i) => (
                <React.Fragment key={i}>
                  {JSON.stringify(entry, null, 2)}
                  {'\n\n'}
                </React.Fragment>
              ))}
            </pre>
          )}
        </div>
      ) : null}
    </div>
  );
}

